import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Play, Volume2, X } from 'lucide-react';
import { useDemo } from './ui';

const BASE = '/driveline/video/driveline-tour';
export const TOUR_POSTER = `${BASE}.jpg`;
const LANGS = [['en', 'EN', ''], ['hi', 'हिंदी', '-hi'], ['kn', 'ಕನ್ನಡ', '-kn']];
const COPY = {
  en: { unmute: 'Unmute', cta: 'Book a demo' },
  hi: { unmute: 'आवाज़ चालू करें', cta: 'डेमो बुक करें' },
  kn: { unmute: 'ಧ್ವನಿ ಆನ್ ಮಾಡಿ', cta: 'ಡೆಮೋ ಬುಕ್ ಮಾಡಿ' },
};

const LangSwitch = ({ lang, onPick }) => (
  <div className="absolute left-0 top-0 flex rounded-full bg-white/10 p-1" role="tablist" aria-label="Video language" data-testid="tour-lang-switch">
    {LANGS.map(([k, label]) => (
      <button key={k} type="button" role="tab" aria-selected={lang === k} onClick={() => onPick(k)}
        className={`rounded-full px-3.5 py-1 text-sm font-semibold transition-colors ${lang === k ? 'bg-white text-slate-900' : 'text-white/80 hover:text-white'}`}
        data-testid={`tour-lang-${k}`}>{label}</button>
    ))}
  </div>
);

// Plays with sound when the browser allows it; otherwise starts muted and offers an Unmute button.
const usePlayback = (ref, lang) => {
  const [muted, setMuted] = useState(false);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    let live = true;
    v.load();
    v.muted = false;
    v.play().then(() => live && setMuted(false)).catch((e) => {
      // AbortError = superseded by a newer load() (lang switch / StrictMode re-run), not an autoplay block.
      if (!live || e?.name === 'AbortError') return;
      v.muted = true;
      setMuted(true);
      v.play().catch(() => {});
    });
    return () => { live = false; };
  }, [ref, lang]);
  const unmute = () => { const v = ref.current; if (!v) return; v.muted = false; setMuted(false); v.play().catch(() => {}); };
  return { muted, setMuted, unmute };
};

export const TourDialog = ({ onClose, lang: initial = 'en' }) => {
  const ref = useRef(null);
  const { open } = useDemo();
  const [lang, setLang] = useState(initial);
  const [cta, setCta] = useState(false);
  const { muted, setMuted, unmute } = usePlayback(ref, lang);
  const suffix = LANGS.find(([k]) => k === lang)[2];
  const t = COPY[lang];
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const onTime = (e) => { const v = e.currentTarget; setCta(v.duration > 0 && v.duration - v.currentTime < 9); };
  const book = () => { onClose(); open(); };
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(11,18,32,.82)] p-3 backdrop-blur-md sm:p-8" onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="tour-dialog">
      <div className="relative w-full max-w-5xl pt-12" style={{ animation: 'dlTourIn .35s ease' }}>
        <LangSwitch lang={lang} onPick={setLang} />
        <div className="relative">
          <video ref={ref} poster={`${BASE}${suffix}.jpg`} controls playsInline onTimeUpdate={onTime}
            onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
            className="block aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-2xl" data-testid="tour-video" data-lang={lang}>
            <source src={`${BASE}${suffix}.mp4`} type="video/mp4" />
            <source src={`${BASE}${suffix}.webm`} type="video/webm" />
          </video>
          {muted && (
            <button type="button" onClick={unmute} className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg transition-transform hover:scale-105" data-testid="tour-unmute">
              <Volume2 size={16} /> {t.unmute}
            </button>
          )}
          {cta && (
            <button type="button" onClick={book} className="absolute bottom-14 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#0f766e] px-3 py-2 text-xs font-semibold text-white shadow-xl transition-transform hover:-translate-y-0.5 sm:bottom-16 sm:right-5 sm:gap-2 sm:px-5 sm:py-3 sm:text-sm" style={{ animation: 'dlTourIn .35s ease' }} data-testid="tour-book-demo">
              {t.cta} <ArrowRight size={16} />
            </button>
          )}
        </div>
        <button type="button" onClick={onClose} className="absolute right-0 top-0 rounded-full bg-white/15 p-2 text-white transition-colors hover:bg-white/30" aria-label="Close video" data-testid="tour-close"><X size={18} /></button>
      </div>
    </div>, document.body);
};

const WATCH = { en: 'Watch the 90-sec tour', hi: '90 सेकंड का टूर देखें', kn: '90 ಸೆಕೆಂಡ್ ಟೂರ್ ನೋಡಿ' };

export const TourButton = ({ lang = 'en', className = '', testid = 'tour-open' }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setShow(true)} className={`inline-flex items-center gap-2 rounded-full border border-[#cbd5e1] px-5 py-3.5 text-[15px] font-semibold text-[#0f172a] transition-colors hover:border-[#0f172a] ${className}`} data-testid={testid}>
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0f766e] text-white"><Play size={11} fill="currentColor" /></span>{WATCH[lang]}
      </button>
      {show && <TourDialog lang={lang} onClose={() => setShow(false)} />}
    </>
  );
};
